import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";

const AgeGroupContext = createContext(null);

// Default theme (middle school)
const DEFAULT_THEME = {
  age_group_id: "middle",
  name: "Middle School (6-8)",
  icon: "🚀",
  mascot: "🦊",
  ui_config: {
    theme: "energetic",
    font_size: "medium",
    color_palette: "vibrant",
    primary_color: "#6C3CE1",
    secondary_color: "#06B6D4",
    accent_color: "#F59E0B",
    background: "gradient-cool",
    animations: "smooth",
    button_style: "rounded-xl",
    icon_style: "modern",
    card_style: "interactive",
    sounds_enabled: false,
    celebration_effects: true,
    reading_mode: "standard"
  },
  content_config: {
    vocabulary_level: "intermediate",
    use_emojis: true,
    gamification_level: "high"
  },
  nav_config: {
    show_icons: true,
    show_labels: true,
    icon_size: "medium",
    simplified_menu: false
  },
  quick_prompts: [],
  subjects: {}
};

export function AgeGroupProvider({ children }) {
  const [ageGroup, setAgeGroup] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);
  const [isSet, setIsSet] = useState(false);

  const fetchAgeGroup = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/age-group/current`, {
        withCredentials: true
      });
      setAgeGroup(response.data);
      setIsSet(response.data.is_set || false);
      applyTheme(response.data.ui_config);
    } catch (error) {
      applyTheme(DEFAULT_THEME.ui_config);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAgeGroup = async (ageGroupId) => {
    try {
      const response = await axios.post(
        `${API}/age-group/set`,
        { age_group: ageGroupId },
        { withCredentials: true }
      );
      setAgeGroup(response.data);
      setIsSet(true);
      applyTheme(response.data.ui_config);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const applyTheme = (uiConfig) => {
    if (!uiConfig) return;
    
    const root = document.documentElement;
    
    // Apply CSS variables
    root.style.setProperty("--age-primary", uiConfig.primary_color);
    root.style.setProperty("--age-secondary", uiConfig.secondary_color);
    root.style.setProperty("--age-accent", uiConfig.accent_color);
    
    // Font size
    const fontSizeMap = { large: "18px", medium: "16px", standard: "14px" };
    root.style.setProperty("--age-font-base", fontSizeMap[uiConfig.font_size] || "16px");
    
    // Border radius
    const radiusMap = { 
      "rounded-full": "9999px", 
      "rounded-xl": "12px", 
      "rounded-lg": "8px",
      "rounded-md": "6px"
    };
    root.style.setProperty("--age-radius", radiusMap[uiConfig.button_style] || "12px");
    
    // Animation duration
    const animMap = { bouncy: "0.5s", smooth: "0.3s", subtle: "0.2s", minimal: "0.1s" };
    root.style.setProperty("--age-anim-duration", animMap[uiConfig.animations] || "0.3s");
    
    // Store theme class on body
    document.body.dataset.ageTheme = uiConfig.theme;
  };

  useEffect(() => {
    fetchAgeGroup();
  }, [fetchAgeGroup]);

  const value = {
    ageGroup,
    loading,
    isSet,
    updateAgeGroup,
    refreshAgeGroup: fetchAgeGroup,
    // Convenience helpers
    isPlayful: ageGroup?.age_group_id === "k5",
    isMiddle: ageGroup?.age_group_id === "middle",
    isHigh: ageGroup?.age_group_id === "high",
    isYoungAdult: ageGroup?.age_group_id === "young_adult",
    useEmojis: ageGroup?.content_config?.use_emojis ?? true,
    showCelebrations: ageGroup?.ui_config?.celebration_effects ?? true,
    quickPrompts: ageGroup?.quick_prompts || [],
    subjects: ageGroup?.subjects || {}
  };

  return (
    <AgeGroupContext.Provider value={value}>
      {children}
    </AgeGroupContext.Provider>
  );
}

export function useAgeGroup() {
  const context = useContext(AgeGroupContext);
  if (!context) {
    throw new Error("useAgeGroup must be used within AgeGroupProvider");
  }
  return context;
}

export default AgeGroupContext;
